import produce, { enablePatches, produceWithPatches } from 'immer';
import * as Y from 'yjs';

function isJSONPrimitive(v) {
  const t = typeof v;
  return t === "string" || t === "number" || t === "boolean" || v === null;
}
function isJSONArray(v) {
  return Array.isArray(v);
}
function isJSONObject(v) {
  return !isJSONArray(v) && typeof v === "object";
}
function toYDataType(v) {
  if (isJSONPrimitive(v)) {
    return v;
  } else if (isJSONArray(v)) {
    const arr = new Y.Array();
    applyJsonArray(arr, v);
    return arr;
  } else if (isJSONObject(v)) {
    const map = new Y.Map();
    applyJsonObject(map, v);
    return map;
  } else {
    return void 0;
  }
}
function applyJsonArray(dest, source) {
  dest.push(source.map(toYDataType));
}
function applyJsonObject(dest, source) {
  Object.entries(source).forEach(([k, v]) => {
    dest.set(k, toYDataType(v));
  });
}
function toPlainValue(v) {
  if (v instanceof Y.Map || v instanceof Y.Array) {
    return v.toJSON();
  } else {
    return v;
  }
}
function notImplemented() {
  throw new Error("not implemented");
}

enablePatches();
function applyYEvent(base, event) {
  if (event instanceof Y.YMapEvent && isJSONObject(base)) {
    const source = event.target;
    event.changes.keys.forEach((change, key) => {
      switch (change.action) {
        case "add":
        case "update":
          base[key] = toPlainValue(source.get(key));
          break;
        case "delete":
          delete base[key];
          break;
      }
    });
  } else if (event instanceof Y.YArrayEvent && isJSONArray(base)) {
    const arr = base;
    let retain = 0;
    event.changes.delta.forEach((change) => {
      if (change.retain) {
        retain += change.retain;
      }
      if (change.delete) {
        arr.splice(retain, change.delete);
      }
      if (change.insert) {
        if (Array.isArray(change.insert)) {
          arr.splice(retain, 0, ...change.insert.map(toPlainValue));
        } else {
          arr.splice(retain, 0, toPlainValue(change.insert));
        }
        retain += change.insert.length;
      }
    });
  }
}
function applyYEvents(snapshot, events) {
  return produce(snapshot, (target) => {
    for (const event of events) {
      const base = event.path.reduce((obj, step) => {
        return obj[step];
      }, target);
      applyYEvent(base, event);
    }
  });
}
const PATCH_REPLACE = "replace";
const PATCH_ADD = "add";
const PATCH_REMOVE = "remove";
function defaultApplyPatch(target, patch) {
  const { path, op, value } = patch;
  if (!path.length) {
    if (op !== PATCH_REPLACE) {
      notImplemented();
    }
    if (target instanceof Y.Map && isJSONObject(value)) {
      target.clear();
      for (const k in value) {
        target.set(k, toYDataType(value[k]));
      }
    } else if (target instanceof Y.Array && isJSONArray(value)) {
      target.delete(0, target.length);
      target.push(value.map(toYDataType));
    } else {
      notImplemented();
    }
    return;
  }
  let base = target;
  for (let i = 0; i < path.length - 1; i++) {
    const step = path[i];
    base = base.get(step);
  }
  const property = path[path.length - 1];
  if (base instanceof Y.Map && typeof property === "string") {
    switch (op) {
      case PATCH_ADD:
      case PATCH_REPLACE:
        base.set(property, toYDataType(value));
        break;
      case PATCH_REMOVE:
        base.delete(property);
        break;
    }
  } else if (base instanceof Y.Array && typeof property === "number") {
    switch (op) {
      case PATCH_ADD:
        base.insert(property, [toYDataType(value)]);
        break;
      case PATCH_REPLACE:
        base.delete(property);
        base.insert(property, [toYDataType(value)]);
        break;
      case PATCH_REMOVE:
        base.delete(property);
        break;
    }
  } else if (base instanceof Y.Array && property === "length") {
    if (value < base.length) {
      const diff = base.length - value;
      base.delete(value, diff);
    }
  } else {
    notImplemented();
  }
}
function applyUpdate(source, snapshot, fn, applyPatch) {
  const [, patches] = produceWithPatches(snapshot, fn);
  for (const patch of patches) {
    applyPatch(source, patch);
  }
}
function bind(source, options) {
  let snapshot = source.toJSON();
  const get = () => snapshot;
  const subscription = /* @__PURE__ */ new Set();
  const subscribe = (fn) => {
    subscription.add(fn);
    return () => void subscription.delete(fn);
  };
  const observer = (events) => {
    snapshot = applyYEvents(get(), events);
    subscription.forEach((fn) => fn(get()));
  };
  source.observeDeep(observer);
  const unbind = () => source.unobserveDeep(observer);
  const applyPatchInOption = options ? options.applyPatch : void 0;
  const applyPatch = applyPatchInOption ? (target, patch) => applyPatchInOption(target, patch, defaultApplyPatch) : defaultApplyPatch;
  const update = (fn) => {
    const doc = source.doc;
    const doApplyUpdate = () => {
      applyUpdate(source, get(), fn, applyPatch);
    };
    if (doc) {
      Y.transact(doc, doApplyUpdate);
    } else {
      doApplyUpdate();
    }
  };
  return {
    unbind,
    get,
    update,
    subscribe
  };
}

export { applyJsonArray, applyJsonObject, bind };
//# sourceMappingURL=immer-yjs.es.js.map
